import {
  CompaniesCountTotalDataTransformer,
  DashboardDataTransformer,
  KEuroToEuroValueDataTransformer,
  PercentageValueDataTransformer,
  RankDashboardDataTransformer,
  SortByNumericValueColumnDashboardDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import { DashboardFilter } from '../../../../filters/dashboard_filter';
import { Metric } from '../../../../dashboard-config/dashboard-config.model';
import {
  KpiColumnDataTransformer,
  KpiLabelDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import {
  IndexIndicatorColumnDataTransformer,
  IndexIndicatorLabelDataTransformer,
  INDICATOR_FILTER_NAME,
} from '../../../../dashboard-config/kpis/index-indicators';
import {
  companiesRevenueProfile,
  economicParticipantsProfile,
  employedProfile,
  gvaProfile,
} from '../../../../dashboard-config/metrics/metric-profile-implementations';
import { MetricProfile } from '../../../../dashboard-config/metrics/metric-profile';
import {
  createIndexFilters,
  INDEX_VALUE_COUNT_KEY,
  INDEX_VALUE_EMPLOYED_KEY,
  INDEX_VALUE_EMPLOYED_STATE_KEY,
  INDEX_VALUE_KEY,
  INDEX_VALUE_SALES_KEY,
} from '../index-metric.profile';
import {
  IndexRankingDevelopmentPopupView,
  IndexRankingDevelopmentValueDisplay,
} from './dashboard-index-ranking-development.definition';
import {
  DashboardIndexRankingDevelopmentShell,
  YearBarChartFilterKind,
} from './dashboard-index-ranking-development-shell.profile';

export {
  INDEX_VALUE_KEY as VALUE_KEY,
  INDEX_VALUE_COUNT_KEY as VALUE_COUNT_KEY,
  INDEX_VALUE_SALES_KEY as VALUE_SALES_KEY,
  INDEX_VALUE_EMPLOYED_KEY as VALUE_EMPLOYED_KEY,
  INDEX_VALUE_EMPLOYED_STATE_KEY as VALUE_EMPLOYED_STATE_KEY,
};

const VALUE_KEY = INDEX_VALUE_KEY;
const VALUE_COUNT_KEY = INDEX_VALUE_COUNT_KEY;
const VALUE_SALES_KEY = INDEX_VALUE_SALES_KEY;
const VALUE_EMPLOYED_KEY = INDEX_VALUE_EMPLOYED_KEY;
const VALUE_EMPLOYED_STATE_KEY = INDEX_VALUE_EMPLOYED_STATE_KEY;

export interface IndexDevelopmentMetricProfile {
  metricId: Metric;
  /** Shared metric profile: KPIs, labels, temporal kind. */
  base: MetricProfile;
  popup: (
    shell: DashboardIndexRankingDevelopmentShell,
  ) => IndexRankingDevelopmentPopupView;
  stateAddSumGermany: boolean;
  excludeSumSubMarketsOnWz: boolean;
  valueCountKey?: (
    shell: DashboardIndexRankingDevelopmentShell,
  ) => string | undefined;
  valueSalesKey?: string;
  valueEmployedKey?: string;
  valueEmployedStateKey?: string;
  valueDisplay: (
    shell: DashboardIndexRankingDevelopmentShell,
  ) => IndexRankingDevelopmentValueDisplay;
  valueColWidth: (shell: DashboardIndexRankingDevelopmentShell) => number;
  /** Control kind for chart-row year filter `year_bar_chart`. */
  resolveMaxYearOffset: (shell: DashboardIndexRankingDevelopmentShell) => number;
  /** Metric lead before the granularity heading words (RANKING/ENTWICKLUNG + metric). */
  headingMetric: (
    filterValues: Map<string, any>,
    shell: DashboardIndexRankingDevelopmentShell,
  ) => string;
  createMetricFilters: (
    shell: DashboardIndexRankingDevelopmentShell,
  ) => DashboardFilter<any>[];
  createChartValueTransformers: (
    valueKey: string,
    shell: DashboardIndexRankingDevelopmentShell,
  ) => DashboardDataTransformer[];
  createDisplayTransformers: (
    shell: DashboardIndexRankingDevelopmentShell,
  ) => DashboardDataTransformer[];
  createRankTransformers: (
    valueKey: string,
    shell: DashboardIndexRankingDevelopmentShell,
  ) => DashboardDataTransformer[];
  createExportTransformers: (
    valueKey: string,
    shell: DashboardIndexRankingDevelopmentShell,
  ) => DashboardDataTransformer[];
  createPreFilterExtras?: () => DashboardDataTransformer[];
}

function yearsRangeLabel(filterValues: Map<string, any>): string {
  const yearsLineChart = filterValues.get('years_line_chart');
  return yearsLineChart ? yearsLineChart.join(' - ') : '';
}

export const indexDevelopmentCompaniesRevenueProfile: IndexDevelopmentMetricProfile =
  {
    metricId: Metric.CompaniesRevenue,
    base: companiesRevenueProfile,
    popup: () => ({ slot: 'revenue', kEur: true }),
    stateAddSumGermany: true,
    excludeSumSubMarketsOnWz: true,
    valueCountKey: () => INDEX_VALUE_COUNT_KEY,
    valueSalesKey: INDEX_VALUE_SALES_KEY,
    valueDisplay: (shell) => shell === 'wzClasses' ? 'kEurShort' : 'kEurLong',
    valueColWidth: (shell) => (shell === 'wzClasses' ? 90 : 105),
    resolveMaxYearOffset: (shell) => shell === 'districtSubMarkets' ? -1 : 0,
    headingMetric: (fv) => {
      const yearsRange = yearsRangeLabel(fv);
      return `RANKING (${fv.get('year_bar_chart')}) UND ENTWICKLUNG (${yearsRange}) ${fv.get(INDICATOR_FILTER_NAME)}`;
    },
    createMetricFilters: () => createIndexFilters(companiesRevenueProfile, { type: 'none' }),
    createChartValueTransformers: (valueKey) => [
      new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
      new IndexIndicatorColumnDataTransformer({
        resultColumn: VALUE_COUNT_KEY,
        source: { impliedMetric: Metric.CompaniesCount },
      }),
      new IndexIndicatorColumnDataTransformer({
        resultColumn: VALUE_SALES_KEY,
        source: { impliedMetric: Metric.CompaniesRevenue },
      }),
    ],
    createDisplayTransformers: () => [new IndexIndicatorLabelDataTransformer()],
    createRankTransformers: (valueKey, shell) => {
      const ranks = [
        new RankDashboardDataTransformer(
          'companies_rank',
          'revenue_per_company',
        ),
        new RankDashboardDataTransformer(
          'small_companies_rank',
          'revenue_per_small_company',
        ),
      ];
      if (shell === 'wzClasses') {
        ranks.push(new RankDashboardDataTransformer('rank', valueKey));
      }
      return ranks;
    },
    createExportTransformers: (valueKey, shell) => {
      const exports: DashboardDataTransformer[] = [
        new KEuroToEuroValueDataTransformer('revenue_per_company'),
        new KEuroToEuroValueDataTransformer('revenue_per_small_company'),
        new KEuroToEuroValueDataTransformer('companies_revenue__k_eur'),
        new KEuroToEuroValueDataTransformer('small_companies_revenue__k_eur'),
      ];
      if (shell === 'subMarkets') {
        exports.push(
          new SortByNumericValueColumnDashboardDataTransformer(valueKey, 'desc'),
        );
      }
      return exports;
    },
  };

export const indexDevelopmentGrossValueAddedProfile: IndexDevelopmentMetricProfile =
  {
    metricId: Metric.GrossValueAdded,
    base: gvaProfile,
    popup: () => ({ slot: 'gva' }),
    stateAddSumGermany: true,
    excludeSumSubMarketsOnWz: false,
    valueDisplay: (shell) =>
      shell === 'wzClasses' ? 'grossValueAddedIndexShort' : 'grossValueAddedIndexLong',
    valueColWidth: (shell) => (shell === 'wzClasses' ? 90 : 105),
    resolveMaxYearOffset: () => 0,
    headingMetric: (fv) => {
      const yearsRange = yearsRangeLabel(fv);
      return `RANKING (${fv.get('year_bar_chart')}) UND ENTWICKLUNG (${yearsRange}) ${fv.get(INDICATOR_FILTER_NAME)}`;
    },
    createMetricFilters: () => createIndexFilters(gvaProfile, { type: 'none' }),
    createChartValueTransformers: (valueKey) => [
      new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
    ],
    createDisplayTransformers: () => [],
    createRankTransformers: (valueKey) => [
      new RankDashboardDataTransformer('rank', valueKey),
    ],
    createExportTransformers: (valueKey) => [
      new KEuroToEuroValueDataTransformer(
        'gross_value_added_per_total_company__k_eur',
      ),
      new KEuroToEuroValueDataTransformer(
        'gross_value_added_per_employed_total__k_eur',
      ),
      new KEuroToEuroValueDataTransformer(
        'gross_value_added_per_economic_participants_total__k_eur',
      ),
      new KEuroToEuroValueDataTransformer(
        'gross_value_added_per_employed_social_security__k_eur',
      ),
      new KEuroToEuroValueDataTransformer(
        'gross_value_added_per_economic_participants_core_market__k_eur',
      ),
      new PercentageValueDataTransformer(
        'gross_value_added_production_value_pct',
      ),
      new KEuroToEuroValueDataTransformer('gross_value_added__k_eur'),
      new KEuroToEuroValueDataTransformer('production_value__k_eur'),
      new SortByNumericValueColumnDashboardDataTransformer(valueKey, 'desc'),
    ],
    createPreFilterExtras: () => [new CompaniesCountTotalDataTransformer()],
  };

export const indexDevelopmentEconomicParticipantProfile: IndexDevelopmentMetricProfile =
  {
    metricId: Metric.EconomicParticipant,
    base: economicParticipantsProfile,
    popup: () => ({ slot: 'economic_participants' }),
    stateAddSumGermany: false,
    excludeSumSubMarketsOnWz: true,
    valueDisplay: () => 'economicParticipantsIndex',
    valueColWidth: () => 85,
    resolveMaxYearOffset: () => 0,
    headingMetric: (fv, shell) => {
      const yearsRange = yearsRangeLabel(fv);
      if (shell === 'wzClasses') {
        return `RANKING (${fv.get('year_bar_chart')}) & ENTWICKLUNG (${yearsRange}) DER ${economicParticipantsProfile.formatKpiHeadingLabel(fv).replace('ter', 'ten')} PRO UNTERNEHMEN`;
      }
      return `RANKING (${fv.get('year_bar_chart')}) UND ENTWICKLUNG (${yearsRange}) ${fv.get(INDICATOR_FILTER_NAME)}`;
    },
    createMetricFilters: (shell) =>
      createIndexFilters(economicParticipantsProfile, {
        type: 'hidden',
        excludedIndicators: shell === 'wzClasses' ? ['economic_participants_total_population_pct'] : [],
      }),
    createChartValueTransformers: (valueKey) => [
      new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
    ],
    createDisplayTransformers: () => [new KpiLabelDataTransformer()],
    createRankTransformers: (valueKey) => [
      new RankDashboardDataTransformer('rank', valueKey),
    ],
    createExportTransformers: (valueKey, shell) => {
      const exports: DashboardDataTransformer[] = [
        new KEuroToEuroValueDataTransformer('companies_revenue__k_eur'),
        new KEuroToEuroValueDataTransformer(
          'revenue_per_economic_participants_total__k_eur',
        ),
      ];
      if (shell === 'subMarkets') {
        exports.push(
          new PercentageValueDataTransformer(
            'economic_participants_total_population_pct',
          ),
        );
      }
      exports.push(
        new PercentageValueDataTransformer(
          'employed_total_economic_participants_total_pct',
        ),
        new SortByNumericValueColumnDashboardDataTransformer(valueKey, 'desc'),
      );
      return exports;
    },
  };

export const indexDevelopmentEmployedProfile: IndexDevelopmentMetricProfile = {
  metricId: Metric.Employed,
  base: employedProfile,
  popup: (shell) =>
    shell === 'wzClasses' ? { slot: 'employedWz' } : { slot: 'employed' },
  stateAddSumGermany: false,
  excludeSumSubMarketsOnWz: true,
  valueCountKey: (shell) =>
    shell === 'wzClasses' ? 'companies_count' : undefined,
  valueEmployedKey: VALUE_EMPLOYED_KEY,
  valueEmployedStateKey: VALUE_EMPLOYED_STATE_KEY,
  valueDisplay: (shell) => shell === 'wzClasses' ? 'oneDecimal' : 'employedPctAware',
  valueColWidth: () => 35,
  resolveMaxYearOffset: () => 0,
  headingMetric: (fv, shell) => {
    const yearsRange = yearsRangeLabel(fv);
    if (shell === 'wzClasses') {
      return `RANKING (${fv.get('year_bar_chart')}) & ENTWICKLUNG (${yearsRange}) DER ${employedProfile.formatKpiHeadingLabel(fv).replace('ter', 'ten')} PRO UNTERNEHMEN`;
    }
    return `RANKING (${fv.get('year_bar_chart')}) UND ENTWICKLUNG (${yearsRange}) DER ANZAHL AN ${employedProfile.formatKpiHeadingLabel(fv)} ${fv.get(INDICATOR_FILTER_NAME)}`;
  },
  createMetricFilters: (shell) =>
    createIndexFilters(employedProfile, {
      type: 'visible',
      excludedIndicators:
        shell === 'wzClasses'
          ? ['employed_state_pct', 'employed_population_pct', 'employed_district_pct']
          : shell === 'districtSubMarkets'
            // idx__d exposes district_pct + population_pct only (no per-company / state_pct)
            ? ['employed_per_company', 'employed_state_pct']
            : ['employed_district_pct'],
    }),
  // Value: the selected type × indicator; employed counts of the row and its state / district.
  createChartValueTransformers: (valueKey, shell) => {
    if (shell === 'wzClasses') {
      return [
        new KpiColumnDataTransformer({ measure: 'value', resultColumn: VALUE_EMPLOYED_KEY }),
        new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
      ];
    }
    return [
      new IndexIndicatorColumnDataTransformer({ resultColumn: valueKey }),
      new KpiColumnDataTransformer({ measure: 'value', resultColumn: VALUE_EMPLOYED_KEY }),
      new KpiColumnDataTransformer({
        measure: shell === 'districtSubMarkets' ? 'districtValue' : 'stateValue',
        resultColumn: VALUE_EMPLOYED_STATE_KEY,
      }),
    ];
  },
  createDisplayTransformers: (shell) =>
    shell === 'wzClasses'
      ? [new KpiLabelDataTransformer()]
      : [new KpiLabelDataTransformer(), new IndexIndicatorLabelDataTransformer()],
  createRankTransformers: (valueKey) => [
    new RankDashboardDataTransformer('rank', valueKey),
  ],
  createExportTransformers: (valueKey, shell) => {
    if (shell === 'wzClasses') {
      return [];
    }
    return [
      new PercentageValueDataTransformer(
        shell === 'districtSubMarkets'
          ? 'employed_total_district_pct'
          : 'employed_total_state_pct',
      ),
      new PercentageValueDataTransformer('employed_total_population_pct'),
      new SortByNumericValueColumnDashboardDataTransformer(valueKey, 'desc'),
    ];
  },
};

export function buildIndexDevelopmentDefaultPreFilterTransformers(
  metric: IndexDevelopmentMetricProfile,
): DashboardDataTransformer[] {
  return [
    new UndefinedStateToSumGermanyDashboardDataTransformer(),
    ...(metric.createPreFilterExtras?.() ?? []),
  ];
}
